import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import "./QRPrintModal.css";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when the asset has been disposed of.
 * Checks both the status string and the isDisposed boolean flag.
 */
const isAssetDisposed = (asset) =>
  asset?.status === "Disposed" || asset?.isDisposed === true;

/**
 * Converts any Firestore Timestamp, plain Date, or seconds-based object
 * to a DD-MM-YY string.  Returns "N/A" for anything it cannot parse.
 */
const formatDateDDMMYY = (timestamp) => {
  if (!timestamp) return "N/A";

  let date;

  // 1. Handle Firestore Timestamps (.toDate() method)
  if (typeof timestamp.toDate === "function") {
    date = timestamp.toDate();
  }
  // 2. Handle Firestore-like objects with a seconds property
  else if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  }
  // 3. Handle Native Date Objects
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // 4. Handle Strings or Numbers (ISO strings, milliseconds, numeric strings)
  else if (typeof timestamp === "string" || typeof timestamp === "number") {
    // If it's a numeric string (e.g., "1785283200000"), convert it to a number first
    const isNumeric = !isNaN(timestamp) && !isNaN(parseFloat(timestamp));
    date = new Date(isNumeric ? Number(timestamp) : timestamp);
  }
  // 5. Fallback for unsupported object types
  else {
    return "N/A";
  }

  // 6. Final safety check: Ensure the parsed date is actually valid
  if (isNaN(date.getTime())) {
    return "N/A";
  }

  // 7. Format the valid date to DD-MM-YY
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);

  return `${dd}-${mm}-${yy}`;
};

/**
 * Rasterises an SVG element to a PNG data-URL at a fixed export resolution.
 * Returns a Promise<string> (data-URL).
 */
const svgToBase64 = (svgElement) =>
  new Promise((resolve) => {
    const EXPORT_RES = 512;
    const canvas = document.createElement("canvas");
    canvas.width = EXPORT_RES;
    canvas.height = EXPORT_RES;
    const ctx = canvas.getContext("2d");
    const img = new Image();

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, EXPORT_RES, EXPORT_RES);
      ctx.drawImage(img, 0, 0, EXPORT_RES, EXPORT_RES);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };

    img.src = url;
  });

// ─────────────────────────────────────────────────────────────────────────────
// PRINT WINDOW HTML BUILDERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the full HTML string that is written into the popup print window.
 *
 * @param {Array<{ asset: object, base64: string }>} qrImages
 * @param {number} currentQrSize  – initial QR box size in cm
 * @param {number} defaultCols    – initial labels-per-row
 * @param {number} defaultGap     – initial gap in cm
 */
const buildPrintWindowHTML = (
  qrImages,
  currentQrSize,
  defaultCols,
  defaultGap,
) => {
  const itemsHTML = qrImages
    .map(({ asset, base64 }) => {
      if (!base64) return ""; // skip assets whose SVG could not be rasterised

      const imageSrc = base64.startsWith("data:")
        ? base64
        : `data:image/png;base64,${base64}`;

      return `
        <div class="qr-code-item">
          <span class="asset-id-header">${asset.assetId ?? ""}</span>
          <div class="qr-img-wrapper">
            <img src="${imageSrc}" alt="QR Code for ${asset.assetId ?? ""}" />
          </div>
          <div class="info-row">
            <span><strong>P:${formatDateDDMMYY(asset.purchaseDate)}</strong></span>
            &nbsp;
            <span><strong>W:${formatDateDDMMYY(asset.warrantyExpiry)}</strong></span>
          </div>
        </div>`;
    })
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Print QR Codes (${qrImages.length})</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

    /* ── CSS custom properties ── */
    :root {
      --grid-cols:   ${defaultCols};
      --qr-box-size: ${currentQrSize}cm;
      --h-gap:       ${defaultGap}cm;
      --v-gap:       ${defaultGap}cm;
      --item-border: 1px solid #000;
    }

    /* ── Reset ── */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Inter', Arial, sans-serif;
      background: #f1f5f9;
      min-height: 100vh;
    }

    /* ── Sticky control panel ── */
    .top-panel {
      position: sticky;
      top: 0;
      z-index: 10000;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      padding: 12px 24px;
      background: #ffffff;
      border-bottom: 2px solid #334155;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .controls-group {
      display: flex;
      align-items: center;
      gap: 15px;
      flex-wrap: wrap;
    }

    .control-box {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .control-box label {
      font-size: 9px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .control-box input,
    .control-box select {
      width: 70px;
      padding: 6px;
      border: 1.5px solid #e2e8f0;
      border-radius: 6px;
      font-weight: 600;
      font-size: 12px;
      color: #1e293b;
      background: #f8fafc;
      outline: none;
      transition: border-color 0.15s;
    }

    .control-box input:focus,
    .control-box select:focus {
      border-color: #6366f1;
    }

    .btn-group { display: flex; gap: 8px; }

    .btn {
      padding: 8px 18px;
      border-radius: 6px;
      font-weight: 700;
      font-size: 13px;
      cursor: pointer;
      border: none;
      transition: opacity 0.15s;
    }

    .btn:hover { opacity: 0.85; }
    .btn-cancel { background: #f1f5f9; color: #475569; }
    .btn-print  { background: #2563eb; color: #ffffff; }

    /* ── QR grid ── */
    .qr-grid {
      display: grid;
      grid-template-columns: repeat(var(--grid-cols), 1fr);
      column-gap: var(--h-gap);
      row-gap:    var(--v-gap);
      padding: 8px;
      width: 100%;
      justify-items: center;
    }

    /* ── Individual QR label card ── */
    .qr-code-item {
      background: #ffffff;
      border: var(--item-border);
      width:  var(--qr-box-size);
      height: var(--qr-box-size);
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0;
      overflow: hidden;
      text-align: center;
    }

    .asset-id-header {
      font-weight: 800;
      color: #000;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      width: 100%;
      padding: 0 2px;
      font-size: calc(var(--qr-box-size) * 0.09);
    }

    .qr-img-wrapper {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 0;
    }

    .qr-img-wrapper img {
      max-width:  100%;
      max-height: 100%;
      object-fit: contain;
    }

    .info-row {
      font-size: calc(var(--qr-box-size) * 0.06);
      line-height: 1.3;
      color: #000;
      width: 100%;
      padding: 1px 2px;
    }

    /* ── Print overrides ── */
    @media print {
      .top-panel          { display: none !important; }
      body                { background: white; }

      .qr-grid {
        display: flex;
        flex-wrap: wrap;
        gap: var(--h-gap) var(--v-gap);
        padding: 0;
      }

      .qr-code-item {
        break-inside: avoid;
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <!-- ── Sticky control panel ── -->
  <div class="top-panel">
    <div class="controls-group">

      <div class="control-box">
        <label>Labels / Row</label>
        <input
          type="number"
          value="${defaultCols}"
          min="1"
          max="20"
          oninput="document.documentElement.style.setProperty('--grid-cols', this.value)"
        />
      </div>

      <div class="control-box">
        <label>Size (cm)</label>
        <input
          type="number"
          step="0.1"
          value="${currentQrSize}"
          oninput="document.documentElement.style.setProperty('--qr-box-size', this.value + 'cm')"
        />
      </div>

      <div class="control-box">
        <label>H. Gap (cm)</label>
        <input
          type="number"
          step="0.1"
          value="${defaultGap}"
          oninput="document.documentElement.style.setProperty('--h-gap', this.value + 'cm')"
        />
      </div>

      <div class="control-box">
        <label>V. Gap (cm)</label>
        <input
          type="number"
          step="0.1"
          value="${defaultGap}"
          oninput="document.documentElement.style.setProperty('--v-gap', this.value + 'cm')"
        />
      </div>

      <div class="control-box">
        <label>Border</label>
        <select
          onchange="document.documentElement.style.setProperty(
            '--item-border',
            this.value === 'on' ? '1px solid #000' : 'none'
          )"
        >
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
      </div>

    </div>

    <div class="btn-group">
      <button class="btn btn-cancel" onclick="window.close()">Close</button>
      <button class="btn btn-print"  onclick="window.print()">Print</button>
    </div>
  </div>

  <!-- ── QR label grid ── -->
  <div class="qr-grid">
    ${itemsHTML}
  </div>
</body>
</html>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const QRPrintModal = ({ isOpen, onClose, selectedAssets, onNotification }) => {
  const [qrSize, setQrSize] = useState(2.4);
  const qrRefs = useRef({});

  const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

  // ── Derived lists ──────────────────────────────────────────────────────────
  const printableAssets = useMemo(
    () => (selectedAssets ?? []).filter((a) => !isAssetDisposed(a)),
    [selectedAssets],
  );

  const disposedAssetsCount = useMemo(
    () => (selectedAssets ?? []).filter((a) => isAssetDisposed(a)).length,
    [selectedAssets],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleQrSizeChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value > 0) setQrSize(value);
  };

  // ── Core print action ──────────────────────────────────────────────────────
  const printQRCodes = useCallback(async () => {
    // ── Guard: nothing printable ───────────────────────────────────────────
    if (!printableAssets.length) {
      const msg =
        disposedAssetsCount > 0 &&
        selectedAssets?.length === disposedAssetsCount
          ? "Cannot print QR codes for disposed assets."
          : "No printable QR codes selected.";
      const type =
        disposedAssetsCount > 0 &&
        selectedAssets?.length === disposedAssetsCount
          ? "error"
          : "warning";
      onNotification(msg, type);
      return;
    }

    try {
      // ── 1. Rasterise every visible QR SVG to PNG ────────────────────────
      const qrImages = await Promise.all(
        printableAssets.map(async (asset) => {
          const container = qrRefs.current[asset.id];
          const svgEl = container?.querySelector("svg");
          const base64 = svgEl ? await svgToBase64(svgEl) : null;
          return { asset, base64 };
        }),
      );

      // Drop any assets whose SVG reference was missing
      const validImages = qrImages.filter(({ base64 }) => Boolean(base64));

      if (!validImages.length) {
        onNotification(
          "Could not capture QR images. Please try again.",
          "error",
        );
        return;
      }

      // ── 2. Open popup window ────────────────────────────────────────────
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        onNotification(
          "Popup blocked. Please allow popups for this site and try again.",
          "error",
        );
        return;
      }

      // ── 3. Write HTML into popup ────────────────────────────────────────
      const DEFAULT_COLS = 1;
      const DEFAULT_GAP = 0.5;

      printWindow.document.write(
        buildPrintWindowHTML(validImages, qrSize, DEFAULT_COLS, DEFAULT_GAP),
      );
      printWindow.document.close();

      // ── 4. Clean up and notify ──────────────────────────────────────────
      onClose();
      onNotification(
        `Print window opened with ${validImages.length} QR label${validImages.length !== 1 ? "s" : ""}.`,
        "success",
      );
    } catch (err) {
      console.error("[QR PRINT] printQRCodes failed:", err);
      onNotification("Failed to open print window. Please try again.", "error");
    }
  }, [
    printableAssets,
    disposedAssetsCount,
    selectedAssets,
    qrSize,
    onNotification,
    onClose,
  ]);

  // ── Auto-trigger when modal opens ─────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Small delay so React has time to render the QR SVGs into the DOM
    const timer = setTimeout(printQRCodes, 500);
    return () => clearTimeout(timer);
  }, [isOpen, printQRCodes]);

  // ── Don't render when closed ───────────────────────────────────────────────
  if (!isOpen) return null;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="qr-print-modal-overlay">
      <div className="qr-print-modal">
        {/* ── Header ── */}
        <div className="qr-print-modal-header no-print">
          <h3>
            Print QR Codes
            {printableAssets.length > 0 && ` (${printableAssets.length})`}
          </h3>
          <button
            className="qr-print-modal-close"
            onClick={onClose}
            aria-label="Close print modal"
          >
            <i className="bi bi-x" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="qr-print-modal-body">
          {/* Disposed-assets warning banner */}
          {disposedAssetsCount > 0 && (
            <div
              className="disposed-assets-warning"
              role="alert"
              style={{
                background: "#fff3cd",
                border: "1px solid #ffeaa7",
                color: "#856404",
                padding: "12px 14px",
                borderRadius: "6px",
                marginBottom: "16px",
                fontSize: "13px",
                lineHeight: "1.5",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <i className="bi bi-exclamation-triangle-fill" />
                <strong>
                  {disposedAssetsCount} disposed asset
                  {disposedAssetsCount !== 1 ? "s" : ""}{" "}
                  {disposedAssetsCount !== 1 ? "were" : "was"} excluded from
                  printing.
                </strong>
              </div>
              <small
                style={{ display: "block", marginTop: "4px", opacity: 0.85 }}
              >
                QR codes are not available for disposed assets.
              </small>
            </div>
          )}

          {/* Size selector + info row */}
          <div className="print-controls no-print">
            <div className="qr-size-selector">
              <label htmlFor="qrSize">QR Code Size:</label>
              <select id="qrSize" value={qrSize} onChange={handleQrSizeChange}>
                <option value={2.4}>Small (2.4 × 2.4 cm)</option>
                <option value={4}>Medium (4 × 4 cm)</option>
                <option value={5}>Large (5 × 5 cm)</option>
              </select>
            </div>

            <div className="print-info">
              <p>
                {printableAssets.length} QR code
                {printableAssets.length !== 1 ? "s" : ""} ready to print
                {disposedAssetsCount > 0 && (
                  <span
                    style={{
                      color: "#856404",
                      fontSize: "12px",
                      display: "block",
                      marginTop: "3px",
                    }}
                  >
                    ({disposedAssetsCount} disposed{" "}
                    {disposedAssetsCount !== 1 ? "assets" : "asset"} excluded)
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* ── QR preview grid (source of truth for SVG rasterisation) ── */}
          <div className="qr-code-grid">
            {printableAssets.map((asset) => {
           const assetUrl = `${BASE_URL}/public-asset/${asset.id}?token=${asset.qrToken}`;

              return (
                <div
                  key={asset.id}
                  className="qr-code-item"
                  ref={(el) => {
                    if (el) qrRefs.current[asset.id] = el;
                  }}
                >
                  <p className="asset-id">{asset.assetId}</p>

                  <QRCodeSVG
                    value={assetUrl}
                    size={Math.round((qrSize / 2.54) * 96)}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    level="H"
                    includeMargin
                  />

                  <p className="extra-small">
                    P:{" "}
                    {asset.purchaseDate
                      ? formatDateDDMMYY(asset.purchaseDate)
                      : "N/A"}
                    {" · "}
                    W:{" "}
                    {asset.warrantyExpiry
                      ? formatDateDDMMYY(asset.warrantyExpiry)
                      : "N/A"}
                  </p>
                </div>
              );
            })}

            {/* Empty-state: all selected assets are disposed */}
            {printableAssets.length === 0 &&
              (selectedAssets?.length ?? 0) > 0 && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    textAlign: "center",
                    padding: "48px 20px",
                    color: "#6c757d",
                  }}
                >
                  <i
                    className="bi bi-exclamation-circle"
                    style={{
                      fontSize: "28px",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  />
                  <strong>No printable QR codes available.</strong>
                  <br />
                  <small>All selected assets have been disposed.</small>
                </div>
              )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="qr-print-modal-footer no-print">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>

          <button
            className="btn-primary"
            onClick={printQRCodes}
            disabled={printableAssets.length === 0}
          >
            <i className="bi bi-printer me-1" />
            Print Asset QR
            {printableAssets.length > 0 && ` (${printableAssets.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRPrintModal;
